import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts, useCategories, useUpdateStock, useDeleteProduct, useUpdateProduct, useUpdateCategory, useBulkUpdatePricesByIds, useRecalculateListPrices } from '@/hooks/useProducts';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { productsService } from '@/services/products';
import { useSuppliers } from '@/hooks/useSuppliers';
import { Product, ProductCategory } from '@/types';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { Plus, Search, Edit, Package, AlertTriangle, Trash2, CreditCard, Calculator, History, Settings, TrendingUp, CheckSquare, Square, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ProductForm from '@/components/ProductForm';
import CostHistoryPanel from '@/components/CostHistoryPanel';
import CategoryFormModal from '@/components/CategoryFormModal';
import { formatPrice } from '@/utils/format';

// Funciones para calcular precios sugeridos
const calculateSalePrice = (costPrice: number): number => {
  const doubled = costPrice * 2;
  return Math.ceil(doubled / 100) * 100;
};

const calculateListPrice = (salePrice: number, roundTo: number = 100): number => {
  const rawPrice = salePrice * 1.10;
  return Math.ceil(rawPrice / roundTo) * roundTo;
};

const SPECIES_FILTER_OPTIONS = [
  { value: '', label: 'Todas las especies' },
  { value: 'Canino', label: 'Canino' },
  { value: 'Felino', label: 'Felino' },
  { value: 'Otro', label: 'Otro' },
];

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'Productos y Servicios' },
  { value: 'products', label: 'Solo Productos' },
  { value: 'services', label: 'Solo Servicios' },
];

export default function Products() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [selectedSpecies, setSelectedSpecies] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [showLowStock, setShowLowStock] = useState(searchParams.get('low_stock') === 'true');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  // Producto escaneado en la lista: se ofrece elegir qué hacer (editar / inventario).
  const [scanChoice, setScanChoice] = useState<Product | null>(null);

  // Permisos: editar productos (ABM) vs editar inventario (stock)
  const { user } = useAuthStore();
  // Con precios por medio de pago prendido se muestran los tres niveles en la lista
  const priceByPayment = useAuthStore((s) => s.tenant?.price_by_payment_method ?? false);
  const canWrite = user?.effective_permissions?.includes('products_write') ?? false;
  const canInventory =
    canWrite || (user?.effective_permissions?.includes('inventory') ?? false);
  const [stockModal, setStockModal] = useState<{
    product: Product;
    quantity: number;
    newCost: string;
    newSalePrice: string;
    newListPrice: string;
    newInstallmentPrice: string;
    updatePrices: boolean;
  } | null>(null);
  const [deleteModal, setDeleteModal] = useState<Product | null>(null);
  const [historyModal, setHistoryModal] = useState<Product | null>(null);
  const [categoryModal, setCategoryModal] = useState<ProductCategory | null>(null);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  // Estado para selección múltiple y actualización masiva
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);

  const { data: products, isLoading } = useProducts({
    search: search || undefined,
    category_id: selectedCategory ? Number(selectedCategory) : undefined,
    supplier_id: selectedSupplier ? Number(selectedSupplier) : undefined,
    only_low_stock: showLowStock,
  });
  const { data: categories } = useCategories();
  const { data: suppliers } = useSuppliers();
  const updateStock = useUpdateStock();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();
  const updateCategory = useUpdateCategory();
  const bulkUpdatePrices = useBulkUpdatePricesByIds();
  const recalculateListPrices = useRecalculateListPrices();

  const handleStockUpdate = async () => {
    if (!stockModal) return;
    try {
      // Usar updateProduct para ambos casos (con o sin cambio de precios)
      // Esto permite registrar el historial de costos siempre
      if (stockModal.updatePrices && stockModal.newCost) {
        // Con actualización de precios
        await updateProduct.mutateAsync({
          id: stockModal.product.id,
          data: {
            cost_price: parseFloat(stockModal.newCost),
            sale_price: stockModal.newSalePrice ? parseFloat(stockModal.newSalePrice) : undefined,
            list_price: stockModal.newListPrice ? parseFloat(stockModal.newListPrice) : undefined,
            installment_price: stockModal.newInstallmentPrice
              ? parseFloat(stockModal.newInstallmentPrice)
              : undefined,
          },
          options: {
            quantity_purchased: stockModal.quantity,
          },
        });
      } else {
        // Sin cambio de precios, pero registrar el ingreso de stock en historial
        await updateProduct.mutateAsync({
          id: stockModal.product.id,
          data: {}, // Sin cambios de precio
          options: {
            quantity_purchased: stockModal.quantity,
          },
        });
      }

      setStockModal(null);
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  const handleDeleteProduct = async () => {
    if (!deleteModal) return;
    try {
      await deleteProduct.mutateAsync(deleteModal.id);
      setDeleteModal(null);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const categoryOptions = [
    { value: '', label: 'Todas las categorías' },
    ...(categories?.map((c) => ({ value: c.id.toString(), label: c.name })) || []),
  ];

  const supplierOptions = [
    { value: '', label: 'Todos los proveedores' },
    ...(suppliers?.map((s) => ({ value: s.id.toString(), label: s.name })) || []),
  ];

  // Filtrar productos por especie y tipo (client-side)
  const filteredProducts = products?.filter((product) => {
    // Filtro por especie
    if (selectedSpecies) {
      if (product.applicable_species && !product.applicable_species.split(',').includes(selectedSpecies)) {
        return false;
      }
    }
    // Filtro por tipo (producto vs servicio)
    if (selectedType === 'products' && product.is_service) return false;
    if (selectedType === 'services' && !product.is_service) return false;
    return true;
  });

  // Limpiar selección cuando cambian los filtros
  useEffect(() => {
    setSelectedProducts(new Set());
  }, [selectedCategory, selectedSupplier, selectedSpecies, selectedType, showLowStock, search]);

  // IDs de productos filtrados para el "seleccionar todo"
  const filteredProductIds = useMemo(() => {
    return filteredProducts?.map(p => p.id) || [];
  }, [filteredProducts]);

  const allSelected = filteredProductIds.length > 0 && filteredProductIds.every(id => selectedProducts.has(id));
  const someSelected = selectedProducts.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProductIds));
    }
  };

  const toggleProductSelection = (productId: number) => {
    const newSelection = new Set(selectedProducts);
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      newSelection.add(productId);
    }
    setSelectedProducts(newSelection);
  };

  // --- Lector de código de barras ---
  // Al escanear, abre la ficha del producto para editarlo.
  const handleBarcodeScan = async (code: string) => {
    let product: Product | null = products?.find((p) => p.barcode === code) || null;
    if (!product) {
      try {
        product = await productsService.getProductByBarcode(code);
      } catch {
        toast.error('Error al buscar el código de barras');
        return;
      }
    }
    if (!product) {
      toast.error(`No hay un producto con el código ${code}`);
      return;
    }
    // No abrimos directo: ofrecemos elegir qué hacer con el producto escaneado.
    setScanChoice(product);
  };

  const handleEditScannedProduct = () => {
    if (!scanChoice) return;
    setEditingProduct(scanChoice);
    setIsModalOpen(true);
    setScanChoice(null);
  };

  const handleEditScannedInventory = () => {
    if (!scanChoice) return;
    setStockModal({
      product: scanChoice,
      quantity: 0,
      newCost: scanChoice.cost_price?.toString() || '',
      newSalePrice: scanChoice.sale_price?.toString() || '',
      newListPrice: scanChoice.list_price?.toString() || '',
      newInstallmentPrice: scanChoice.installment_price?.toString() || '',
      updatePrices: false,
    });
    setScanChoice(null);
  };

  // Solo activo cuando no hay ningún modal abierto (así el escaneo DENTRO del
  // formulario de producto va al campo "código de barras", no acá).
  const anyModalOpen =
    isModalOpen ||
    !!stockModal ||
    !!deleteModal ||
    !!historyModal ||
    !!categoryModal ||
    !!scanChoice ||
    showCategoriesModal ||
    showBulkUpdateModal;

  useBarcodeScanner(handleBarcodeScan, { enabled: !anyModalOpen });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Productos</h1>
          <p className="text-gray-600">Gestiona tu inventario de productos y servicios</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowCategoriesModal(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Categorías
            </Button>
            <Button onClick={() => setIsModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Producto
            </Button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar productos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 min-w-[180px]">
            <Select
              options={categoryOptions}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="flex-1 min-w-[150px]"
            />
            {canWrite && selectedCategory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const cat = categories?.find(c => c.id.toString() === selectedCategory);
                  if (cat) setCategoryModal(cat);
                }}
                title="Configurar multiplicador"
                className="px-2"
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Select
            options={supplierOptions}
            value={selectedSupplier}
            onChange={(e) => setSelectedSupplier(e.target.value)}
            className="min-w-[170px]"
          />
          <Select
            options={SPECIES_FILTER_OPTIONS}
            value={selectedSpecies}
            onChange={(e) => setSelectedSpecies(e.target.value)}
            className="min-w-[150px]"
          />
          <Select
            options={TYPE_FILTER_OPTIONS}
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="min-w-[170px]"
          />
          <label className="flex items-center space-x-2 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showLowStock}
              onChange={(e) => setShowLowStock(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Solo stock bajo</span>
          </label>
        </div>

        {/* Barra de acciones de selección */}
        {canWrite && someSelected && (
          <div className="mt-4 pt-4 border-t flex items-center justify-between bg-blue-50 -mx-6 -mb-6 px-6 py-3">
            <span className="text-sm text-blue-800">
              {selectedProducts.size} producto{selectedProducts.size !== 1 ? 's' : ''} seleccionado{selectedProducts.size !== 1 ? 's' : ''}
            </span>
            <Button onClick={() => setShowBulkUpdateModal(true)}>
              <TrendingUp className="h-4 w-4 mr-2" />
              Actualizar Precios
            </Button>
          </div>
        )}
      </div>

      {/* Lista de productos */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : filteredProducts && filteredProducts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {canWrite && (
                    <th className="px-4 py-3 text-left">
                      <button
                        onClick={toggleSelectAll}
                        className="text-gray-500 hover:text-gray-700"
                        title={allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                      >
                        {allSelected ? (
                          <CheckSquare className="h-5 w-5 text-primary-600" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Categoría
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Especies
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Precio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Vencimiento
                  </th>
                  {canWrite && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product) => {
                  const isLowStock = product.track_stock && Number(product.current_stock) <= Number(product.minimum_stock);
                  const isSelected = selectedProducts.has(product.id);
                  return (
                    <tr
                      key={product.id}
                      className={clsx('hover:bg-gray-50', canWrite && 'cursor-pointer', isSelected && 'bg-blue-50')}
                      onClick={() => {
                        // Solo abre edición si tiene permiso de ABM de productos
                        if (!canWrite) return;
                        setEditingProduct(product);
                        setIsModalOpen(true);
                      }}
                    >
                      {canWrite && (
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => toggleProductSelection(product.id)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-primary-600" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                              {product.source_product_id && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-normal bg-teal-100 text-teal-800 flex-shrink-0"
                                  title={`Fraccionado de ${product.source_product_name || 'un producto a granel'}`}
                                >
                                  Fraccionado
                                </span>
                              )}
                              {product.is_bulk && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-normal bg-amber-100 text-amber-800 flex-shrink-0">
                                  A granel
                                </span>
                              )}
                              {product.name}
                            </div>
                            {product.source_product_id ? (
                              <div className="text-xs text-gray-500">
                                Sale de {product.source_product_name || '—'}
                                {product.fraction_quantity
                                  ? ` · ${Math.round(Number(product.fraction_quantity) * 1000)} g por unidad`
                                  : ''}
                              </div>
                            ) : product.code ? (
                              <div className="text-sm text-gray-500">{product.code}</div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.category?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {product.applicable_species ? (
                          <div className="flex flex-wrap gap-1">
                            {product.applicable_species.split(',').map((sp) => (
                              <span
                                key={sp}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                              >
                                {sp}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">Todas</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div>
                          <span className="text-gray-900 font-medium">
                            {formatPrice(product.sale_price)}
                          </span>
                          {priceByPayment ? (
                            <>
                              <span className="block text-xs text-blue-600">
                                <CreditCard className="h-3 w-3 inline mr-1" />
                                {formatPrice(product.list_price ?? product.sale_price)}
                              </span>
                              <span className="block text-xs text-purple-600">
                                <CreditCard className="h-3 w-3 inline mr-1" />
                                {formatPrice(
                                  product.installment_price ?? product.list_price ?? product.sale_price
                                )}{' '}
                                en cuotas
                              </span>
                            </>
                          ) : (
                            product.list_price != null && product.list_price !== product.sale_price && (
                              <span className="block text-xs text-blue-600">
                                <CreditCard className="h-3 w-3 inline mr-1" />
                                {formatPrice(product.list_price)}
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {product.is_service ? (
                          <span className="text-gray-400">-</span>
                        ) : product.track_stock ? (
                          canInventory ? (
                            <button
                              onClick={() => setStockModal({
                                product,
                                quantity: 0,
                                newCost: product.cost_price?.toString() || '',
                                newSalePrice: product.sale_price?.toString() || '',
                                newListPrice: product.list_price?.toString() || '',
                                newInstallmentPrice: product.installment_price?.toString() || '',
                                updatePrices: false,
                              })}
                              className={clsx(
                                'inline-flex items-center px-2 py-1 rounded text-sm',
                                isLowStock
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                              )}
                            >
                              {isLowStock && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {product.current_stock}
                            </button>
                          ) : (
                            <span
                              className={clsx(
                                'inline-flex items-center px-2 py-1 rounded text-sm',
                                isLowStock
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                              )}
                            >
                              {isLowStock && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {product.current_stock}
                            </span>
                          )
                        ) : product.source_product_id ? (
                          // Se puede cargar stock desde el fraccionado: entra al granel
                          <button
                            onClick={() => canInventory && setStockModal({
                              product,
                              quantity: 0,
                              newCost: product.cost_price?.toString() || '',
                              newSalePrice: product.sale_price?.toString() || '',
                              newListPrice: product.list_price?.toString() || '',
                              newInstallmentPrice: product.installment_price?.toString() || '',
                              updatePrices: false,
                            })}
                            disabled={!canInventory}
                            className={clsx(
                              'inline-flex items-center px-2 py-1 rounded text-sm',
                              Number(product.source_current_stock ?? 0) <= 0
                                ? 'bg-red-100 text-red-800'
                                : 'bg-teal-100 text-teal-800'
                            )}
                            title="Stock del producto a granel del que sale. Tocá para cargar paquetes que entraron."
                          >
                            {Number(product.source_current_stock ?? 0).toLocaleString('es-AR', {
                              maximumFractionDigits: 2,
                            })}{' '}kg
                            {product.available_units != null && (
                              <span className="ml-1 opacity-70">· {product.available_units} u.</span>
                            )}
                          </button>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.expiration_date || '-'}
                      </td>
                      {canWrite && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setHistoryModal(product)}
                              title="Ver historial de costos"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingProduct(product);
                                setIsModalOpen(true);
                              }}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteModal(product)}
                              title="Eliminar"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No se encontraron productos
          </div>
        )}
      </div>

      {/* Modal de producto */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProduct(null);
        }}
        title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        size="2xl"
      >
        <div className="space-y-4">
          <ProductForm
            product={editingProduct}
            onSuccess={() => {
              setIsModalOpen(false);
              setEditingProduct(null);
            }}
          />
          {/* Historial de costos (solo en edición) */}
          {editingProduct && (
            <div className="border-t pt-4">
              <CostHistoryPanel
                productId={editingProduct.id}
                productName={editingProduct.name}
              />
            </div>
          )}
        </div>
      </Modal>

      {/* Elección al escanear un producto en la lista */}
      <Modal
        isOpen={!!scanChoice}
        onClose={() => setScanChoice(null)}
        title="Producto escaneado"
        size="sm"
      >
        {scanChoice && (
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
              <p className="font-semibold text-gray-900">{scanChoice.name}</p>
              <p className="text-sm text-gray-500">
                Stock: {scanChoice.current_stock ?? 0}
                {scanChoice.code ? ` · Cód: ${scanChoice.code}` : ''}
              </p>
            </div>
            <p className="text-sm text-gray-500">¿Qué querés hacer?</p>
            <div className="grid grid-cols-1 gap-2">
              {canWrite && (
                <button
                  onClick={handleEditScannedProduct}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 text-left transition-colors"
                >
                  <Edit className="h-5 w-5 text-taupe-600 flex-shrink-0" />
                  <span className="font-medium text-gray-900">Editar producto</span>
                </button>
              )}
              {canInventory && (
                <button
                  onClick={handleEditScannedInventory}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 text-left transition-colors"
                >
                  <Package className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="font-medium text-gray-900">Editar inventario (stock)</span>
                </button>
              )}
              {!canWrite && !canInventory && (
                <p className="text-sm text-gray-400 text-center py-2">No tenés permiso para editar.</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de stock */}
      <Modal
        isOpen={!!stockModal}
        onClose={() => setStockModal(null)}
        title="Ajustar Stock"
        size="md"
      >
        {stockModal && (
          <StockUpdateModal
            stockModal={stockModal}
            setStockModal={setStockModal}
            onUpdate={handleStockUpdate}
            isLoading={updateStock.isPending || updateProduct.isPending}
            canEditPrices={canWrite}
            priceByPayment={priceByPayment}
          />
        )}
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Eliminar Producto"
        size="sm"
      >
        {deleteModal && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de que deseas eliminar <strong>{deleteModal.name}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Esta acción no se puede deshacer.
            </p>
            <p className="text-sm text-gray-500">
              Su código interno y su código de barras quedan libres para usar en otro producto.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteModal(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteProduct}
                loading={deleteProduct.isPending}
              >
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de historial de costos */}
      <Modal
        isOpen={!!historyModal}
        onClose={() => setHistoryModal(null)}
        title={`Historial de Costos - ${historyModal?.name || ''}`}
        size="lg"
      >
        {historyModal && (
          <CostHistoryPanel
            productId={historyModal.id}
            productName={historyModal.name}
          />
        )}
      </Modal>

      {/* Modal de configuración de categoría (formulario completo) */}
      <Modal
        isOpen={!!categoryModal}
        onClose={() => setCategoryModal(null)}
        title={`Editar Categoría: ${categoryModal?.name || ''}`}
        size="md"
      >
        {categoryModal && (
          <CategoryFormModal
            category={categoryModal}
            onSave={async (data) => {
              await updateCategory.mutateAsync({ id: categoryModal.id, data });
            }}
            onClose={() => setCategoryModal(null)}
            isLoading={updateCategory.isPending}
            onRecalculate={async (categoryId) => {
              try {
                const result = await recalculateListPrices.mutateAsync(categoryId);
                toast.success(`Se actualizaron ${result.updated_count} productos`);
              } catch {
                toast.error('Error al recalcular precios');
              }
            }}
            isRecalculating={recalculateListPrices.isPending}
          />
        )}
      </Modal>

      {/* Modal de lista de categorías */}
      <Modal
        isOpen={showCategoriesModal}
        onClose={() => setShowCategoriesModal(false)}
        title="Configurar Categorías"
        size="md"
      >
        <CategoriesListModal
          categories={categories || []}
          onEditCategory={(cat) => {
            setShowCategoriesModal(false);
            setCategoryModal(cat);
          }}
          onRecalculate={async (categoryId) => {
            try {
              const result = await recalculateListPrices.mutateAsync(categoryId);
              toast.success(`Se actualizaron ${result.updated_count} productos`);
            } catch {
              toast.error('Error al recalcular precios');
            }
          }}
          isRecalculating={recalculateListPrices.isPending}
        />
      </Modal>

      {/* Modal de actualización masiva de precios */}
      <Modal
        isOpen={showBulkUpdateModal}
        onClose={() => setShowBulkUpdateModal(false)}
        title="Actualizar Precios Masivamente"
        size="2xl"
      >
        <BulkPriceUpdateModal
          selectedCount={selectedProducts.size}
          selectedProductIds={Array.from(selectedProducts)}
          products={filteredProducts || []}
          onUpdate={async (percentage, recalculateFromCost, reason) => {
            await bulkUpdatePrices.mutateAsync({
              product_ids: Array.from(selectedProducts),
              percentage,
              recalculate_from_cost: recalculateFromCost,
              reason,
            });
            setShowBulkUpdateModal(false);
            setSelectedProducts(new Set());
          }}
          onClose={() => setShowBulkUpdateModal(false)}
          isLoading={bulkUpdatePrices.isPending}
        />
      </Modal>

    </div>
  );
}

// Componente para el modal de actualización de stock con precios
interface StockModalData {
  product: Product;
  quantity: number;
  newCost: string;
  newSalePrice: string;
  newListPrice: string;
  newInstallmentPrice: string;
  updatePrices: boolean;
}

function StockUpdateModal({
  stockModal,
  setStockModal,
  onUpdate,
  isLoading,
  canEditPrices,
  priceByPayment,
}: {
  stockModal: StockModalData;
  setStockModal: (data: StockModalData | null) => void;
  onUpdate: () => void;
  isLoading: boolean;
  canEditPrices: boolean;
  priceByPayment: boolean;
}) {
  const [localCost, setLocalCost] = useState(stockModal.newCost);
  const [localSalePrice, setLocalSalePrice] = useState(stockModal.newSalePrice);
  const [localListPrice, setLocalListPrice] = useState(stockModal.newListPrice);
  const [localInstallmentPrice, setLocalInstallmentPrice] = useState(stockModal.newInstallmentPrice);
  // La cantidad se edita como texto para poder escribir 6,5 o 6.5 sin que el navegador
  // la rechace (type=number con locale es-AR no acepta el punto).
  // Un fraccionado no tiene stock propio: lo que se carga va al producto a granel
  const isFrac = !!(stockModal.product.source_product_id && stockModal.product.fraction_quantity);
  const parentStock = Number(stockModal.product.source_current_stock ?? 0);
  const fmtStock = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 2 });

  const [quantityText, setQuantityText] = useState(
    stockModal.quantity ? String(stockModal.quantity) : ''
  );

  // Dos formas de corregir el stock, porque se confundian entre si: "agregar" suma lo
  // que entro (el caso de todos los dias) y "dejar en" fija el total contado. Antes solo
  // existia la primera y para un conteo fisico habia que calcular la diferencia a mano;
  // en Inventario el ajuste funciona al reves, asi que el mismo gesto daba resultados
  // distintos segun la pantalla.
  const [stockMode, setStockMode] = useState<'agregar' | 'dejar_en'>('agregar');

  const stockBase = isFrac ? parentStock : Number(stockModal.product.current_stock ?? 0);
  const valorIngresado = parseFloat((quantityText || '').replace(',', '.'));
  const hayValor = !isNaN(valorIngresado);
  // El movimiento que se va a aplicar: en "dejar en" es la diferencia contra lo que hay.
  const ajuste = !hayValor ? 0 : stockMode === 'agregar' ? valorIngresado : valorIngresado - stockBase;
  const stockResultante = stockBase + ajuste;

  // Lo que se guarda es SIEMPRE el movimiento (el delta), no el total escrito: el backend
  // suma lo que reciba. En modo "dejar en" el delta ya viene calculado arriba.
  useEffect(() => {
    setStockModal({ ...stockModal, quantity: ajuste });
    // Solo cuando cambia el movimiento calculado: incluir stockModal en las dependencias
    // lo volveria un bucle, porque este mismo efecto lo reescribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ajuste]);

  // Calcular precios sugeridos cuando cambia el costo
  const suggestedSalePrice = localCost ? calculateSalePrice(parseFloat(localCost)) : null;
  const suggestedListPrice = localSalePrice ? calculateListPrice(parseFloat(localSalePrice)) : null;

  // Sincronizar con el estado padre
  useEffect(() => {
    setStockModal({
      ...stockModal,
      newCost: localCost,
      newSalePrice: localSalePrice,
      newListPrice: localListPrice,
      newInstallmentPrice: localInstallmentPrice,
    });
  }, [localCost, localSalePrice, localListPrice, localInstallmentPrice]);

  const applySuggestedSalePrice = () => {
    if (suggestedSalePrice) {
      setLocalSalePrice(suggestedSalePrice.toString());
    }
  };

  const applySuggestedListPrice = () => {
    if (suggestedListPrice) {
      setLocalListPrice(suggestedListPrice.toString());
    }
  };

  const applyAllSuggested = () => {
    if (suggestedSalePrice) {
      setLocalSalePrice(suggestedSalePrice.toString());
      const newListPrice = calculateListPrice(suggestedSalePrice);
      setLocalListPrice(newListPrice.toString());
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 p-3 rounded-lg">
        <p className="text-gray-600">
          <strong>{stockModal.product.name}</strong>
        </p>
        {isFrac ? (
          <p className="text-sm text-gray-500">
            Stock del granel:{' '}
            <span className="font-medium">{fmtStock(parentStock)}</span>
            {stockModal.product.source_product_name ? ` de ${stockModal.product.source_product_name}` : ''}
          </p>
        ) : (
          <p className="text-sm text-gray-500">
            Stock actual: <span className="font-medium">{stockModal.product.current_stock}</span>
            {stockModal.product.cost_price && (
              <> | Costo actual: <span className="font-medium">{formatPrice(stockModal.product.cost_price)}</span></>
            )}
          </p>
        )}
      </div>

      {/* Es un fraccionado: el ingreso se carga sobre el producto a granel */}
      {isFrac && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800">
          Este es un producto fraccionado: el stock lo lleva el granel. Cargá los{' '}
          <strong>kilos que entraron</strong> —no la cantidad de paquetes— y se suman al
          stock de {stockModal.product.source_product_name || 'el granel'}. Admite decimales:
          una bolsa de 7,5 kg se carga como <strong>7,5</strong>.
        </div>
      )}

      {/* Qué se está haciendo: sumar lo que entró, o fijar el total contado */}
      <div className="grid grid-cols-2 gap-2">
        {([
          { key: 'agregar' as const, titulo: 'Agregar', sub: 'Entró mercadería' },
          { key: 'dejar_en' as const, titulo: 'Dejar en', sub: 'Conteo físico' },
        ]).map((opcion) => (
          <button
            key={opcion.key}
            type="button"
            onClick={() => setStockMode(opcion.key)}
            className={`px-3 py-2 rounded-lg border-2 text-sm transition-colors ${
              stockMode === opcion.key
                ? 'bg-taupe-600 border-taupe-600 text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <span className="block font-medium">{opcion.titulo}</span>
            <span className={`block text-xs ${stockMode === opcion.key ? 'text-white/80' : 'text-gray-400'}`}>
              {opcion.sub}
            </span>
          </button>
        ))}
      </div>

      <Input
        type="text"
        inputMode="decimal"
        label={
          stockMode === 'agregar'
            ? (isFrac ? 'Kilos que entraron al granel' : 'Cantidad que entró')
            : (isFrac ? 'Kilos que hay del granel' : 'Cuánto hay en el estante')
        }
        value={quantityText}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^\d.,-]/g, '');
          setQuantityText(raw);
        }}
        placeholder={
          stockMode === 'agregar'
            ? (isFrac ? 'Ej: 7,5 · 15 · -2' : 'Ej: 10 · 6,5 · -5')
            : (isFrac ? 'Ej: 22,5' : 'Ej: 8')
        }
      />

      <p className="text-xs text-gray-500">
        {stockMode === 'agregar' ? (
          <>Positivos para agregar, negativos para quitar. Admite decimales (6,5).</>
        ) : (
          <>Escribí el total que contaste. El sistema calcula la diferencia.</>
        )}
        {hayValor && (
          <>
            {stockMode === 'dejar_en' && (
              <>
                {' '}Ajuste:{' '}
                <strong className={ajuste < 0 ? 'text-red-600' : 'text-green-700'}>
                  {ajuste > 0 ? '+' : ''}{fmtStock(ajuste)}
                </strong>
                {' '}·
              </>
            )}
            {isFrac ? ' Queda el granel en ' : ' Queda en '}
            <strong>{fmtStock(stockResultante)}</strong>
          </>
        )}
      </p>

      {/* Checkbox para actualizar precios: solo con permiso de ABM de productos.
          En un fraccionado no aplica: su costo se calcula desde el costo del granel. */}
      {canEditPrices && !isFrac && (
        <div className="border-t pt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stockModal.updatePrices}
              onChange={(e) => setStockModal({ ...stockModal, updatePrices: e.target.checked })}
              className="rounded border-gray-300 text-primary-600"
            />
            <span className="text-sm font-medium">Actualizar precios con nuevo costo</span>
          </label>
        </div>
      )}

      {canEditPrices && !isFrac && stockModal.updatePrices && (
        <div className="bg-blue-50 p-4 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-700 flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              Actualizar Precios
            </h4>
            {localCost && (
              <button
                type="button"
                onClick={applyAllSuggested}
                className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
              >
                Aplicar precios sugeridos
              </button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Input
                type="number"
                label="Nuevo Costo"
                value={localCost}
                onChange={(e) => setLocalCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Input
                type="number"
                label="Precio Efectivo"
                value={localSalePrice}
                onChange={(e) => setLocalSalePrice(e.target.value)}
                placeholder="0.00"
              />
              {suggestedSalePrice && localSalePrice !== suggestedSalePrice.toString() && (
                <button
                  type="button"
                  onClick={applySuggestedSalePrice}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  Sugerido: ${suggestedSalePrice.toLocaleString('es-AR')}
                </button>
              )}
            </div>
            <div>
              <Input
                type="number"
                label="Precio Tarjeta"
                value={localListPrice}
                onChange={(e) => setLocalListPrice(e.target.value)}
                placeholder="0.00"
              />
              {suggestedListPrice && localListPrice !== suggestedListPrice.toString() && (
                <button
                  type="button"
                  onClick={applySuggestedListPrice}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  Sugerido: ${suggestedListPrice.toLocaleString('es-AR')}
                </button>
              )}
            </div>
          </div>
          {priceByPayment && (
            <div>
              <Input
                type="number"
                label="Precio Crédito en Cuotas"
                value={localInstallmentPrice}
                onChange={(e) => setLocalInstallmentPrice(e.target.value)}
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si lo dejás vacío, el crédito en cuotas se cobra al precio de tarjeta.
              </p>
            </div>
          )}

          <p className="text-xs text-gray-600">
            Efectivo = Costo × 2 (múltiplo de 100) | Tarjeta = Efectivo + 10%
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={() => setStockModal(null)}>
          Cancelar
        </Button>
        <Button onClick={onUpdate} loading={isLoading}>
          {stockModal.updatePrices ? 'Actualizar Stock y Precios' : 'Actualizar Stock'}
        </Button>
      </div>
    </div>
  );
}

// Componente para listar categorías y sus multiplicadores
function CategoriesListModal({
  categories,
  onEditCategory,
  onRecalculate,
  isRecalculating,
}: {
  categories: ProductCategory[];
  onEditCategory: (category: ProductCategory) => void;
  onRecalculate: (categoryId: number) => void;
  isRecalculating: boolean;
}) {
  // Obtener descripción de la fórmula de efectivo
  const getFormulaDescription = (category: ProductCategory): string => {
    const formula = category.price_formula || 'multiply_round_up';
    if (formula === 'margin_percentage') {
      return `Margen ${category.margin_percentage ?? 30}%`;
    } else if (formula === 'fixed_margin') {
      return `+$${(category.price_multiplier ?? 500).toLocaleString('es-AR')}`;
    } else if (formula === 'multiply_exact') {
      return `×${category.price_multiplier ?? 2} (exacto)`;
    } else {
      return `×${category.price_multiplier ?? 2}`;
    }
  };

  if (categories.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No hay categorías creadas
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600 mb-4">
        Configurá la fórmula de precio para cada categoría
      </p>
      <div className="divide-y">
        {categories.map((category) => (
          <div
            key={category.id}
            className="flex items-center justify-between py-3 hover:bg-gray-50 px-2 rounded"
          >
            <div className="cursor-pointer flex-1" onClick={() => onEditCategory(category)}>
              <p className="font-medium text-gray-900">{category.name}</p>
              <p className="text-sm text-gray-500">
                Efectivo: {getFormulaDescription(category)} → Tarjeta: +{Math.round(((category.list_price_multiplier ?? 1.10) - 1) * 100)}%
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRecalculate(category.id)}
                disabled={isRecalculating}
                title="Recalcular precios de tarjeta de esta categoría"
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEditCategory(category)}>
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Componente para actualización masiva de precios
function BulkPriceUpdateModal({
  selectedCount,
  selectedProductIds,
  products,
  onUpdate,
  onClose,
  isLoading,
}: {
  selectedCount: number;
  selectedProductIds: number[];
  products: Product[];
  onUpdate: (percentage: number, recalculateFromCost: boolean, reason?: string) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}) {
  const [percentage, setPercentage] = useState<string>('10');
  const [recalculateFromCost, setRecalculateFromCost] = useState(true);
  // Si la vete usa precios por medio de pago, la vista previa muestra los tres
  const priceByPayment = useAuthStore((st) => st.tenant?.price_by_payment_method ?? false);
  const [reason, setReason] = useState('');

  // Preview de algunos productos seleccionados
  const previewProducts = useMemo(() => {
    return products
      .filter(p => selectedProductIds.includes(p.id))
      .slice(0, 12);
  }, [products, selectedProductIds]);

  const percentageValue = parseFloat(percentage) || 0;

  // Calcular nuevos precios según la configuración
  const calculateNewPrices = (product: Product) => {
    const costPrice = product.cost_price || 0;
    if (!costPrice) return { salePrice: null, listPrice: null };

    // Nuevo costo con el porcentaje aplicado
    const newCost = costPrice * (1 + percentageValue / 100);

    if (recalculateFromCost && product.category) {
      // Usar fórmula de categoría
      const formula = product.category.price_formula || 'multiply_round_up';
      const multiplier = product.category.price_multiplier || 2.0;
      const marginPct = product.category.margin_percentage || 30;
      const roundTo = product.category.price_round_to || 100;
      const listMultiplier = product.category.list_price_multiplier || 1.10;

      let newSalePrice: number;
      if (formula === 'margin_percentage') {
        // Costo + margen%
        newSalePrice = Math.ceil((newCost * (1 + marginPct / 100)) / roundTo) * roundTo;
      } else if (formula === 'multiply_round_up') {
        newSalePrice = Math.ceil((newCost * multiplier) / roundTo) * roundTo;
      } else if (formula === 'multiply_exact') {
        newSalePrice = Math.round(newCost * multiplier * 100) / 100;
      } else if (formula === 'fixed_margin') {
        newSalePrice = Math.round((newCost + multiplier) * 100) / 100;
      } else {
        newSalePrice = Math.ceil((newCost * 2) / 100) * 100;
      }

      // Precio tarjeta redondeado hacia arriba
      const newListPrice = Math.ceil((newSalePrice * listMultiplier) / roundTo) * roundTo;

      // El de cuotas se carga a mano por producto: la fórmula no lo toca
      return {
        salePrice: newSalePrice,
        listPrice: newListPrice,
        installmentPrice: product.installment_price ?? null,
      };
    } else {
      // Aplicar porcentaje directo sobre los precios de venta
      const factor = 1 + percentageValue / 100;
      const salePrice = product.sale_price || 0;
      const listPrice = product.list_price || 0;
      const installmentPrice = product.installment_price || 0;
      return {
        salePrice: salePrice ? Math.round(salePrice * factor) : null,
        listPrice: listPrice ? Math.round(listPrice * factor) : null,
        installmentPrice: installmentPrice ? Math.round(installmentPrice * factor) : null,
      };
    }
  };

  const handleSubmit = async () => {
    await onUpdate(percentageValue, recalculateFromCost, reason || undefined);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-blue-800">
          Se actualizarán los precios de <strong>{selectedCount} producto{selectedCount !== 1 ? 's' : ''}</strong>
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Porcentaje de ajuste
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              className="w-32"
              placeholder="10"
            />
            <span className="text-gray-500">%</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Usa valores negativos para reducir precios (ej: -10 para 10% menos)
          </p>
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={recalculateFromCost}
              onChange={(e) => setRecalculateFromCost(e.target.checked)}
              className="rounded border-gray-300 text-primary-600"
            />
            <span className="text-sm">Recalcular usando fórmula de categoría</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            <strong>Marcado:</strong> aumenta el <strong>costo</strong> y recalcula el precio de venta
            con la fórmula de la categoría. Usalo cuando te aumenta el proveedor.
            <br />
            <strong>Sin marcar:</strong> aumenta los <strong>precios de venta</strong> (efectivo, tarjeta
            y cuotas) y deja el costo como está. Usalo para un aumento de lista.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motivo (opcional)
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej: Aumento de proveedor"
          />
        </div>
      </div>

      {/* Preview */}
      {previewProducts.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 flex justify-between items-center">
            <span className="flex-1 mr-2">Vista previa (estimado)</span>
            <div className="flex gap-4 text-xs text-gray-500">
              <span className="w-24 text-right">Efectivo</span>
              <span className="w-24 text-right">Tarjeta</span>
              {priceByPayment && <span className="w-24 text-right">Cuotas</span>}
            </div>
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {previewProducts.map(product => {
              const { salePrice, listPrice, installmentPrice } = calculateNewPrices(product);
              return (
                <div key={product.id} className="px-4 py-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 truncate flex-1 mr-2">{product.name}</span>
                    <div className="flex gap-4 text-right">
                      <div className="w-24">
                        <span className="text-gray-400 line-through text-xs block">
                          {formatPrice(product.sale_price)}
                        </span>
                        <span className="text-gray-900 font-medium">
                          {salePrice ? formatPrice(salePrice) : '-'}
                        </span>
                      </div>
                      <div className="w-24">
                        <span className="text-gray-400 line-through text-xs block">
                          {formatPrice(product.list_price)}
                        </span>
                        <span className="text-blue-600 font-medium">
                          {listPrice ? formatPrice(listPrice) : '-'}
                        </span>
                      </div>
                      {priceByPayment && (
                        <div className="w-24">
                          <span className="text-gray-400 line-through text-xs block">
                            {product.installment_price ? formatPrice(product.installment_price) : '-'}
                          </span>
                          <span className="text-purple-600 font-medium">
                            {installmentPrice ? formatPrice(installmentPrice) : '-'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {selectedCount > 12 && (
              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                ... y {selectedCount - 12} más
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!percentage || isLoading}
        >
          <TrendingUp className="h-4 w-4 mr-2" />
          Actualizar {selectedCount} producto{selectedCount !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
}
